/*
 * File: fascicle.service.ts                                                   *
 * Project: catalog-cloud                                                      *
 * File Created: Thursday, 17th February 2022 11:48:25                         *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Monday, 6th March 2023 10:02:36                              *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { firstValueFrom, map, Subject } from 'rxjs';

// import { ModalDialogComponent } from '../../tms-tools/components/modal-dialog/modal-dialog.component';
// import { PopInfoService } from '../../pop-info/services/pop-info-service.service';
import { ApiService } from '../../api/services/api.service';

import { Fascicle } from '@nousmedis/cc-types';
import { FascicleFormComponent } from '../components/fascicle/fascicle-form/fascicle-form.component';
import { ActionsMenuService } from '../../menus/services/actionsMenu.service';
import { FascicleDeleteComponent } from '../components/fascicle/fascicle-delete/fascicle-delete.component';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environment';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { FascicleAutoFormComponent } from '../components/fascicle/fascicle-auto-form/fascicle-auto-form.component';
import { CatalogUploadComponent } from '../components/catalog/catalog-upload/catalog-upload.component';
import { GridLocked } from '../interfaces/grid-locked.interface';

export interface IBlock {
  blockedBy?: string;
  blockedAt?: string;
}

export interface FascicleBlockStatus {
  isLocked: boolean;
  lockedBy?: { id: string; name: string };
  lockedAt?: Date;
}

@Injectable()
export class FascicleService {
  /**
   * Endpoint list fascicles: /catalog/:idCatalog/fascicles
   *
   */
  protected get urlBase(): string { return this.api.getBaseUrl(); }

  get endpoint(): string { return this.urlBase + '/api/v1/catalog/:idCatalog/fascicles'; }
  get endpointCatalog(): string { return this.urlBase + '/api/v1/catalogs/'; }

  // Behaviour properties for edit properties and create fascicle

  loadingFascicle: Subject<boolean> = new Subject<boolean>();
  dataFascicles: Subject<Fascicle[]> = new Subject<Fascicle[]>();
  dataTableFascicles: Subject<Fascicle[]> = new Subject<Fascicle[]>();
  lockButtons: Subject<GridLocked> = new Subject<GridLocked>();

  toolbarData: {
    idCatalog: string;
    idFascicle: string;
    filter: any;
  };

  toolbarData$: Subject<any> = new Subject<any>();

  constructor (
    private api: ApiService,
    private dialog: DialogService,
    private messageService: MessageService,
    private actionMenuService: ActionsMenuService,
    private trans: TranslateService,
    private router: Router,
  ) { }

  private getBaseEndPoint(idCatalog: string) {
    return this.urlBase + '/api/v1/catalog/' + idCatalog + '/fascicles';
  }

  /**
   * Abre el formulario de la creación del fasciculo
   * @param id
   * @param idCatalog
   * @returns
   */
  openFascicleForm(id: string, idCatalog: string) {
    return this.dialog.open(FascicleFormComponent, {
      data: {
        id: id == '' ? undefined : id,
        idCatalog: idCatalog,
      },
      header:
        id.length > 0
          ? this.trans.instant('FASCICLE.EDIT_FASCICLE')
          : this.trans.instant('FASCICLE.NEW_FASCICLE'),
      modal: true,
      resizable: true,
    });
  }

  openFascicleAutoForm(id: string, idCatalog: string) {
    return this.dialog.open(FascicleAutoFormComponent, {
      data: {
        id: id,
        idCatalog: idCatalog,
      },
      header: this.trans.instant('FASCICLE.AUTOMATIC_GENERATION'),
      modal: true,
      resizable: true,
    });
  }

  /**
   * Devuelve los fasciculos de un catalogo
   * @param idCatalog
   * @returns
   */
  fetch(idCatalog: string) {
    return this.api.get(this.getBaseEndPoint(idCatalog));
  }

  /**
   * Devuelve un fasciculo
   * @param idCatalog
   * @param id
   * @returns
   */
  get(idCatalog: string, id: string) {
    let url = this.getBaseEndPoint(idCatalog) + '/' + id;
    return this.api.get(this.getBaseEndPoint(idCatalog) + '/' + id);
  }

  /**
   * Guarda un fasciculo
   * @param fascicle
   * @returns
   */

  save(fascicle: Fascicle) {
    if (!fascicle.id || fascicle.id == null || fascicle.id.length == 0) {
      return this.api.post(this.endpoint, fascicle);
    } else {
      return this.api.put(this.endpoint + '/' + fascicle.id, fascicle);
    }
  }

  /**
   * Elimina un fasciculo
   * @param idCatalog
   * @param id
   * @returns
   */
  delete(idCatalog: string, id: string) {
    const endpoint = this.getBaseEndPoint(idCatalog) + '/' + id;
    return this.api.delete(endpoint);
  }

  /**
   * Clona un fasciculo
   * @param idCatalog
   * @param idFascicle
   * @returns
   */
  clone(idCatalog: string, idFascicle: string) {
    let body = { modelFascicleReference: idFascicle };
    const endpoint = this.getBaseEndPoint(idCatalog) + '/clone/' + idFascicle;
    return this.api.post(endpoint, body);
  }

  /**
   *
   * @param data Form data provided by hiddenFields and mainfields (Mainfields has the data, hiddenfields the id)
   */
  saveProperties(data: any) {
    let url = this.getBaseEndPoint(data.idCatalog);
    if (data.id != '' && data.id != undefined) {
      return this.api.put(url + '/' + data.id, data);
    } else {
      let url = this.getBaseEndPoint(data.idCatalog);
      return this.api.post(url, data);
    }
  }

  /**
   * Proceso logico de guardado de un fasciculo
   * @param idCatalog
   * @param id
   * @returns
   */
  saveNewFascicle(formData: any) {
    return new Promise((resolve, reject) => {
      this.saveProperties(formData).subscribe((data) => {
        if (formData.id && formData.id.length > 0) {
          this.messageService.add({
            severity: 'success',
            summary: this.trans.instant('FASCICLE.UPDATED'),
            detail: this.trans.instant('FASCICLE.UPDATED'),
            key: 'msg-root',
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: this.trans.instant('FASCICLE.CREATED'),
            detail: this.trans.instant('FASCICLE.CREATED'),
            key: 'msg-root',
          });
        }
        resolve(data);
      });
    });
  }

  /**
   * Inicializa un fasciculo nuevo
   * @returns
   */
  initialize(idUser: string): Fascicle {
    return {
      name: '',
      id: undefined,
      createdBy: idUser,
      updatedBy: idUser,
      categories: [],
      tags: [],
      blocked: false,
      extra: null,
      pages: null,
      position: 0,
      totalPages: 0,
      blockedBy: undefined,
    };
  }

  /**
   * Actualiza el orden de los fasciculos
   * @param idCatalog
   * @param fascicles
   */
  updateFascicleOrder(idCatalog: string, fascicles: Fascicle[]) {
    const endPoint = this.getBaseEndPoint(idCatalog);
    const totalFascicles = fascicles.length;
    let allLoaded = new Subject();
    let currentLoad = 0;
    allLoaded.subscribe((data) => {
      if (data == totalFascicles) {
        this.api
          .post(this.endpointCatalog + 'recalculate_pages/' + idCatalog, null)
          .toPromise();
      }
    });

    for (let i = 0; i < fascicles.length; i++) {
      fascicles[i].position = i + 1;
      this.api
        .put(endPoint + '/' + fascicles[i].id, {
          position: fascicles[i].position,
        })
        .subscribe((data) => {
          currentLoad = currentLoad + 1;
          allLoaded.next(currentLoad);
        });
    }
  }

  /*
   * Devuelve los fasciculos ordenados
   */
  getFasciclesOrdered(fascicles: Fascicle[]) {
    return fascicles.sort((a, b) =>
      a.position > b.position ? 1 : b.position > a.position ? -1 : 0
    );
  }

  /**
   * Setea el rango de paginas de los fasciculos
   * @param fascicles
   * @returns
   */
  setFromToPages(fascicles: Fascicle[]) {
    fascicles = this.getFasciclesOrdered(fascicles);
    let current = 0;
    for (let i = 0; i < fascicles.length; i++) {
      if (i == 0) {
        if (fascicles[i].totalPages == 0) {
          fascicles[i].fromto = '_EMPTY_';
        } else {
          fascicles[i].fromto = '1-' + fascicles[i].totalPages;
        }
      } else {
        if (fascicles[i].totalPages == 0) {
          fascicles[i].fromto = fascicles[i].fromto = '_EMPTY_';
        } else {
          fascicles[i].fromto =
            current + 1 + '-' + (current + fascicles[i].totalPages);
        }
      }

      current += fascicles[i].totalPages;
    }
    return fascicles;
  }

  /**
   * Editar las propiedades de un fasciculo
   * @param catalogId
   * @param fascicleId
   */
  editFascicleProperties(catalogId: string, fascicleId: string) {
    this.openFascicleForm(fascicleId, catalogId).onClose.subscribe(
      (formData) => {
        // if (formData != false) {
        //   this.loadingFascicle.next(true);
        //   //this.loading =true;
        //   this.saveProperties(formData).subscribe((data) => {
        //     this.fetch(catalogId).subscribe((fascicles) => {
        //       if (formData['mainFieldsGroup']['id'].length > 0)
        //         this.messageService.add(
        //           {
        //             severity: 'success',
        //             summary: this.trans.instant('FASCICLE.UPDATED'),
        //             detail: this.trans.instant('FASCICLE.UPDATED'),
        //           }
        //         );
        //       else
        //         this.messageService.add({
        //           severity: 'success',
        //           summary: this.trans.instant('FASCICLE.CREATED'),
        //           detail: this.trans.instant('FASCICLE.CREATED')
        //         }
        //         );
        //       //let data = this.setFromToPages(fascicles);
        //       let source = this.setFromToPages(fascicles);
        //       this.dataFascicles.next(data);
        //       this.dataTableFascicles.next(source);
        //       this.loadingFascicle.next(false);
        //       this.actionMenuService.reset();
        //     });
        //   });
        // }
      }
    );
  }

  /**
   * Carga los fasciculos de un catalogo
   * @param catalogId
   */

  loadFascicles(catalogId: string) {
    this.fetch(catalogId).subscribe((fascicles) => {
      let data = this.setFromToPages(fascicles);
      this.dataFascicles.next(data);
      this.dataTableFascicles.next(data);
    });
  }

  /**
   * Abre una modal para crear un nuevo fasciculo
   * @param catalogId
   */
  newModalFascicle(catalogId: string) {
    this.router.navigate(['/catalog', catalogId, 'fascicles', 'new']);
  }

  newModalFascicleAuto(catalogId: string) {
    this.openFascicleAutoForm('', catalogId).onClose.subscribe((formData) => {
      this.loadFascicles(catalogId);
    });
  }

  block(idCatalog: string, id: string) {
    const url = this.getBaseEndPoint(idCatalog) + '/' + id + '/block';
    return this.api.put(url, null);
  }

  unblock(idCatalog: string, id: string) {
    const url = this.getBaseEndPoint(idCatalog) + '/' + id + '/unblock';
    return this.api.put(url, null);
  }

  /**
   * Devuelve el estado de bloqueo de un fasciculo de forma estructurada.
   * Retorna null si el fasciculo no está bloqueado.
   */
  getBlockStatus(fascicle: Fascicle): FascicleBlockStatus | null {
    if (!fascicle.blocked) {
      return null;
    }

    const status: FascicleBlockStatus = { isLocked: true };

    if (fascicle.blockedBy) {
      status.lockedBy = { id: fascicle.blockedBy, name: fascicle.blockedBy };
    }

    // blockedAt no está tipado en la interfaz Fascicle pero puede venir
    // del backend via el index signature [x: string]: any
    const rawBlockedAt: unknown = (fascicle as Record<string, unknown>)['blockedAt'];
    if (rawBlockedAt) {
      status.lockedAt = rawBlockedAt instanceof Date
        ? rawBlockedAt
        : new Date(rawBlockedAt as string);
    }

    return status;
  }

  /**
   * Devuelve true solo si el userId coincide con el usuario que bloqueó el fasciculo.
   * Un fasciculo desbloqueado siempre devuelve false (no hay nada que desbloquear).
   */
  canUnblock(userId: string, fascicle: Fascicle): boolean {
    if (!fascicle.blocked || !fascicle.blockedBy) {
      return false;
    }
    return userId === fascicle.blockedBy;
  }

  async getBreadCrumbFascicleList(idCatalog: string, _id: string) {
    const c = await firstValueFrom(
      this.api.get(this.endpointCatalog + idCatalog)
    );
    const _fascicle = c.fascicles.map((f: any) => {
      return { id: f.id, name: f.name };
    });

    const parentBreadcrumb = {
      url: '/',
      label: await this.trans.instant('CATALOG.LIST'),
    };

    const breadcrumb = {
      url: '/catalog/' + idCatalog + '/fascicles',
      label: c.name + ': ' + (await this.trans.instant('FASCICLE.LIST')),
    };
    const breadcrumbs = [parentBreadcrumb, breadcrumb];

    return Promise.resolve(breadcrumbs);
  }

  async getBreadCrumbFascicleEdit(idCatalog: string, id: string) {
    const c = await firstValueFrom(
      this.api.get(this.endpointCatalog + idCatalog)
    );
    const fascicle = c.fascicles
      .map((f: any) => {
        return { id: f.id, name: f.name };
      })
      .filter((f: any) => f.id == id)[0];

    const parentBreadcrumb = {
      url: '/',
      label: await this.trans.instant('CATALOG.LIST'),
    };

    const fasciclesBreadcrumb = {
      url: '/catalog/' + idCatalog + '/fascicles',
      label: c.name + ': ' + (await this.trans.instant('FASCICLE.LIST')),
    };

    const editBreadcrumb = {
      url: '/',
      label:
        (await this.trans.instant('FASCICLE.EDIT_THIS')) + ' ' + fascicle.name,
    };

    const breadcrumbs = [parentBreadcrumb, fasciclesBreadcrumb, editBreadcrumb];

    return Promise.resolve(breadcrumbs);
  }

  async getBreadCrumbFascicleNew(idCatalog: string) {
    const c = await firstValueFrom(this.api.get(this.endpointCatalog + idCatalog));
    return [
      { url: '/', label: await this.trans.instant('CATALOG.LIST') },
      { url: '/catalog/' + idCatalog + '/fascicles', label: c.name + ': ' + await this.trans.instant('FASCICLE.LIST') },
      { label: await this.trans.instant('FASCICLE.NEW_FASCICLE') },
    ];
  }

  setToolbarData(idCatalog: string, idFascicle: string, filter: any) {
    this.toolbarData = {
      idCatalog: idCatalog,
      idFascicle: idFascicle,
      filter: filter,
    };
    this.toolbarData$.next(this.toolbarData);
  }

  //Get BreadCrumb for an edit fascicle
  getBreadCrumbEdit(idCatalog: string, id: string) {
    return new Promise((resolve, _reject) => {
      this.api.get(this.endpointCatalog + idCatalog).subscribe((c) => {
        this.get(idCatalog, id).subscribe((_f) => {
          let fascicles = c.fascicles.map((f: any) => {
            return { id: f.id, name: f.name };
          });
          let breadcrumb = {
            breadcrumb: 'fascicle-edit',
            catalogId: c.id,
            catalogName: c.name,
            fascicles: fascicles,
            currentFascicle: id,
          };
          resolve(breadcrumb);
        });
      });
    });
  }

  //exportar catalogo a syncwall

  openUploadFormToSyncwall(id: string) {
    const titulo = !id ? 'Exportar catálogo' : 'Exportar catálogo';
    this.dialog.open(CatalogUploadComponent, {
      header: titulo,
      maximizable: true,
      modal: true,
      style: { 'min-width': '50rem' },
      closeOnEscape: true,
      data: {
        id: id,
      },
    });
  }
}
